import { useI18n } from "@baton/i18n";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { durationMs } from "../../theme/motionTokens.ts";
import "./Skeleton.css";

/**
 * FR-015-090, FR-015-103, § Loading, taken from shadcn/ui's Skeleton (Spectrum UI's `skeleton`
 * item is its demo; its Skeleton Reveal is a reveal wrapper for screens) and themed through the
 * variables of `packages/tokens` (2026-09-15, by the owner's decision): a rounded fill pulsing
 * on `animate-pulse`, whose loop is the motion token, so reduced motion leaves it still. The
 * text, row, card and avatar variants (the chart variant went with the Team dashboard's charts on 2026-10-21). A screen's skeleton is one `SkeletonRegion`, the
 * single polite "Loading" announcement, holding `SkeletonBlock` shapes laid out in the screen's
 * own geometry; each block is `aria-hidden` and takes its size from `--skeleton-inline-size`
 * and `--skeleton-block-size` on its class, so a layout sizes a block without out-ranking the
 * variant. `Skeleton` is the region with `count` blocks of one variant, for a panel whose final
 * layout is a plain stack; holding blocks alone, its region is a `span`, so it is valid where only
 * phrasing content is, such as an activity row's sentence while the sentence's catalogue loads
 * (2026-10-07), and its `flex` draws it as the block a `div` was. `tone="chrome"` is the tone of every placeholder in the rail, the
 * sidebar, the switcher and the actor card, in the sidebar's hover fill, so no light bar paints
 * on the dark frame.
 *
 * What differs from the source, and why: the work tone fills with `bg-secondary`, where the
 * source's `bg-accent` is the dark theme's work surface colour itself and would not show; and the
 * size comes from the two variables above rather than from caller classes, because the screens'
 * skeleton layouts already set them.
 *
 * **The hold, the one fade and the no-blank rule (2026-10-15, FR-015-194, § Loading).** Every
 * region mounts at once at opacity 0 and holds for `--skeleton-delay`, 150 ms, then fades in over
 * `--duration-base`; a response inside the hold paints its content with no skeleton ever seen. The
 * hold is a component token outside the durations, so reduced motion keeps it and makes only the
 * fade instant. `LoadingRegion` is the region a screen's content lives in: while `loading` it draws
 * the skeleton; when the content arrives after the hold, the content is laid out at its final size
 * with the skeleton over it, and the two cross in one fade of `--duration-base`, the skeleton
 * unmounting when the fade ends; when the content arrives inside the hold, it is drawn at once with
 * no fade, there being nothing on screen to cross from. Once a region has drawn its content it never
 * draws a frame without it: `loading` turning true again changes nothing, and only a new subject,
 * which the host gives the region as its `key`, mounts a skeleton again.
 *
 * A skeleton that takes over from one already on screen takes no hold of its own: a screen's own
 * region mounting as the route's Suspense fallback leaves (`SkeletonHoldScope` in the route table),
 * where a second hold would paint a frame with nothing in it. A scope knows which of its skeletons
 * are visible and when the last one left; a region mounting while one is visible, or within a frame
 * of one leaving, is shown at once, and `LoadingRegion` reads the same scope to know whether its
 * skeleton was ever seen.
 */
export type SkeletonVariant = "text" | "row" | "card" | "avatar";

export type SkeletonTone = "work" | "chrome";

/** A skeleton mounting this soon after one left, in the same scope, continues it rather than holding again. */
const HANDOVER_MS = 50;

interface HoldScope {
  readonly visible: Set<symbol>;
  lastHidden: number;
  readonly parent: HoldScope | null;
}

function newScope(parent: HoldScope | null): HoldScope {
  return { visible: new Set(), lastHidden: Number.NEGATIVE_INFINITY, parent };
}

/** A region outside every scope is a subject of its own: it shares its skeleton with nothing. */
const HoldContext = createContext<HoldScope | null>(null);

/** Whether a skeleton of this scope, or of one it sits in, is on screen or left within the last frame. */
function skeletonOnScreen(scope: HoldScope, now: number): boolean {
  for (let current: HoldScope | null = scope; current !== null; current = current.parent) {
    if (current.visible.size > 0 || now - current.lastHidden <= HANDOVER_MS) return true;
  }
  return false;
}

function markVisible(scope: HoldScope, id: symbol): void {
  scope.visible.add(id);
}

function markHidden(scope: HoldScope, id: symbol, now: number): void {
  if (scope.visible.delete(id)) scope.lastHidden = now;
}

/** The scope a region or a loading region registers in: the nearest provided one, with the parent chain a child scope reads through. */
function useOwnScope(): HoldScope {
  const parent = useContext(HoldContext);
  return useMemo(() => newScope(parent), [parent]);
}

/** One subject's skeletons: a route, or a frame that changes subject on its own (the side peek). */
export function SkeletonHoldScope({ children }: { readonly children: ReactNode }) {
  const scope = useOwnScope();
  return <HoldContext value={scope}>{children}</HoldContext>;
}

export interface SkeletonRegionProps {
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * The hold: the region is drawn at opacity 0 until `--skeleton-delay` passes, then fades in; a region
 * taking over from a skeleton already on screen in its scope is shown at once. The region tells its
 * scope when it is seen and when it leaves.
 */
function useHold(element: React.RefObject<HTMLDivElement | null>): boolean {
  // The region registers in the scope it sits in, or in one of its own where none is provided.
  const provided = useContext(HoldContext);
  const own = useOwnScope();
  const scope = provided ?? own;
  const id = useMemo(() => Symbol("skeleton"), []);
  const [hold] = useState(() => !skeletonOnScreen(scope, performance.now()));
  useLayoutEffect(() => {
    const region = element.current;
    if (region === null) return;
    let shown = false;
    const onShown = () => {
      if (shown) return;
      shown = true;
      markVisible(scope, id);
    };
    // The end of the region's own fade, heard on the element itself: the hold is over and the skeleton is seen.
    const onAnimationEnd = (event: Event) => {
      if (event.target === region) onShown();
    };
    if (hold) region.addEventListener("animationend", onAnimationEnd);
    else onShown();
    return () => {
      region.removeEventListener("animationend", onAnimationEnd);
      if (shown) markHidden(scope, id, performance.now());
    };
    // The hold is decided once, on mount; the scope, the id and the element never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return hold;
}

function Region({ as: Element, className, children }: SkeletonRegionProps & { readonly as: "div" | "span" }) {
  const { t } = useI18n();
  // A `div` ref serves the `span` too: the listener and the attribute it reads are the element's.
  const element = useRef<HTMLDivElement | null>(null);
  const hold = useHold(element);
  return (
    <Element ref={element} data-slot="skeleton-region" data-hold={hold ? "true" : undefined} className={className} role="status" aria-busy="true" aria-label={t("ui.loading")}>
      {children}
    </Element>
  );
}

export function SkeletonRegion(props: SkeletonRegionProps) {
  return <Region as="div" {...props} />;
}

const variants: Readonly<Record<SkeletonVariant, string>> = {
  text: "w-[var(--skeleton-inline-size,100%)] h-[var(--skeleton-block-size,var(--text-body-line))] rounded-md",
  row: "w-[var(--skeleton-inline-size,100%)] h-[var(--skeleton-block-size,var(--row-height))] rounded-md",
  card: "w-[var(--skeleton-inline-size,100%)] h-[var(--skeleton-block-size,calc(var(--row-height)*3))] rounded-lg",
  avatar: "w-[var(--skeleton-inline-size,var(--avatar-lg))] h-[var(--skeleton-block-size,var(--avatar-lg))] rounded-full",
};

export interface SkeletonBlockProps {
  readonly variant?: SkeletonVariant;
  readonly tone?: SkeletonTone;
  readonly className?: string;
}

export function SkeletonBlock({ variant = "text", tone = "work", className }: SkeletonBlockProps) {
  return <span data-slot="skeleton" data-variant={variant} data-tone={tone} aria-hidden="true" className={cn("block animate-pulse", tone === "chrome" ? "bg-sidebar-accent" : "bg-secondary", variants[variant], className)} />;
}

export interface SkeletonProps {
  readonly variant?: SkeletonVariant;
  readonly tone?: SkeletonTone;
  readonly count?: number;
  readonly className?: string;
}

export function Skeleton({ variant = "text", tone = "work", count = 1, className }: SkeletonProps) {
  return (
    <Region as="span" className={cn("flex flex-col gap-3", className)}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonBlock key={i} variant={variant} tone={tone} />
      ))}
    </Region>
  );
}

export interface LoadingRegionProps {
  /** The region's name, which the no-blank run reports a flash by (`data-region`). */
  readonly region: string;
  /** True while the region waits for its first content. Once content is drawn this is read no more: the last content stays until the host changes the region's `key`. */
  readonly loading: boolean;
  /** The skeleton of the content's own geometry, a `SkeletonRegion` of blocks. */
  readonly skeleton: ReactNode;
  readonly className?: string;
  readonly children: ReactNode;
}

type Phase = "skeleton" | "crossing" | "content";

/**
 * The region a screen's content is drawn in: its skeleton while the content is awaited, the content
 * laid out under the skeleton and crossed with it in one fade once it arrives after the hold, or drawn
 * at once when it arrives inside the hold, and never a frame without the content after that.
 */
export function LoadingRegion({ region, loading, skeleton, className, children }: LoadingRegionProps) {
  const scope = useOwnScope();
  const [phase, setPhase] = useState<Phase>(loading ? "skeleton" : "content");
  // The content arrives: a skeleton that was seen is crossed with it, one that was not is dropped. Decided as the
  // render sees `loading` change, so the content's first frame is already the right phase.
  if (!loading && phase === "skeleton") setPhase(scope.visible.size > 0 ? "crossing" : "content");
  // The cross ends when the content's fade ends; where no animation runs (the region hidden, or a test document), it ends after the fade's length.
  const element = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = element.current;
    if (phase !== "crossing" || host === null) return;
    const done = () => {
      setPhase("content");
    };
    const onAnimationEnd = (event: Event) => {
      if (event.target instanceof Element && event.target.parentElement === host && !event.target.classList.contains("b-region__skeleton")) done();
    };
    host.addEventListener("animationend", onAnimationEnd);
    const timer = window.setTimeout(done, durationMs("duration-base"));
    return () => {
      host.removeEventListener("animationend", onAnimationEnd);
      window.clearTimeout(timer);
    };
  }, [phase]);
  return (
    <HoldContext value={scope}>
      <div ref={element} data-region={region} data-phase={phase} className={cn("b-region", className)}>
        {phase !== "skeleton" && children}
        {phase !== "content" && (
          <div className="b-region__skeleton" aria-hidden={phase === "crossing" ? "true" : undefined}>
            {skeleton}
          </div>
        )}
      </div>
    </HoldContext>
  );
}
